<template>
    <div :class="[$style['mds-banner'], classObject, variationClass, sizeClass]" :data-mds-version="version">
        <div :class="$style['mds-banner__body']">
            <mds-icon :name="variationIcon" :class="$style['mds-banner__icon']" :size="iconSize"> </mds-icon>
            <span :class="$style['mds-banner__message']">
                <slot>{{ text }}</slot>
            </span>
        </div>
        <mds-button
            v-if="!persistent"
            :class="$style['mds-banner__dismiss-button']"
            size="small"
            icon="remove"
            @click="$emit('mds-banner-dismissed', $event)"
            variation="icon-only"
            :text="dismissIconAriaLabel"
        >
        </mds-button>
    </div>
</template>

<script>
    import MdsIcon from '@mds/icon';
    import { MdsButton } from '@mds/button';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsBanner',
        props: {
            dismissIconAriaLabel: {
                type: String,
                default: 'Dismiss',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` attribute on the dismiss button.',
                },
            },
            persistent: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, removes the dismiss button that allows the banner to be dismissed.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`',
                    description: 'Sets the size of the banner.',
                },
            },
            variation: {
                type: String,
                default: 'informational',
                validator(value) {
                    return ['error', 'informational', 'warning'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `error`, `informational`, `success`,',
                    description: 'Sets the variation of the banner.',
                },
            },
            text: {
                type: String,
                docs: {
                    validation: '**Required**',
                    description: 'The text that appears in the banner. Can also be passed via the default slot.',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
                docs: {
                    validation: 'Read-only',
                    description:
                        'Set automatically by package version, do not manually adjust this prop. Used to add a data attribute providing the component version to the component markup rendered in the DOM.',
                },
            },
        },
        components: {
            MdsIcon,
            MdsButton,
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-banner--dismissible']] = !this.persistent;
                return obj;
            },
            sizeClass() {
                return this.$style[`mds-banner--${this.size}`];
            },
            iconSize() {
                return this.size === 'small' ? 'small' : 'medium';
            },
            variationClass() {
                return this.$style[`mds-banner--${this.variation}`];
            },
            variationIcon() {
                const icon = {
                    error: 'alert',
                    warning: 'alert',
                    informational: 'info-circle',
                };

                return icon[this.variation];
            },
        },
    };
</script>

<style lang="scss" module>
    @import './banner.scss';
</style>
